import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CustomersList from './CustomersList';
import ViewCustomer from './ViewCustomer';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Customers.css';

function Customers({ initialCustomerId }) {
  const { t } = useTranslation('customers');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  // Handle initial customer ID from cross-module navigation (/customers/:id)
  useEffect(() => {
    if (initialCustomerId) {
      setSelectedCustomerId(parseInt(initialCustomerId, 10));
      setViewMode('view');
    }
  }, [initialCustomerId]);

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('toolbar.titles.customers'),
          buttons: [
            {
              label: t('toolbar.buttons.newCustomer'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('toolbar.titles.viewCustomer'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('toolbar.titles.editCustomer'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('toolbar.titles.newCustomer'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedCustomerId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewCustomer component
    if (window.viewCustomerRef && window.viewCustomerRef.handleDelete) {
      window.viewCustomerRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewCustomer component
    if (window.viewCustomerRef && window.viewCustomerRef.handleSave) {
      window.viewCustomerRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedCustomerId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedCustomerId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in CustomersList
    if (window.customersListRef && window.customersListRef.fetchCustomers) {
      window.customersListRef.fetchCustomers();
    }
  };

  const handleCustomerSaved = (customer) => {
    setSelectedCustomerId(customer.customer_id);
    setViewMode('view');
    // Refresh the list
    if (window.customersListRef && window.customersListRef.fetchCustomers) {
      window.customersListRef.fetchCustomers();
    }
  };

  return (
    <div className="customers-page">
      <ResizablePanels
        leftPanel={
          <CustomersList
            ref={ref => window.customersListRef = ref}
            onSelectCustomer={handleSelectCustomer}
            selectedCustomerId={selectedCustomerId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewCustomer
              ref={ref => window.viewCustomerRef = ref}
              customerId={selectedCustomerId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleCustomerSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Customers;